import { apiRequest, refreshTokens } from './client'
import { useAuthStore } from '../stores/auth'
import type { SignInResponse, UserProfile } from '../types/api'

export async function signIn(email: string, password: string): Promise<void> {
    const data = await apiRequest<SignInResponse>('/auth/signin', {
        method: 'POST',
        body: { email, password },
    })
    useAuthStore.getState().setSession(data.accessToken, data.user)
}

export async function signUp(
    email: string,
    password: string,
    name: string,
    inviteToken?: string,
): Promise<SignInResponse> {
    const data = await apiRequest<SignInResponse>('/auth/signup', {
        method: 'POST',
        body: {
            email,
            password,
            name,
            userLanguage: 'de',
            ...(inviteToken ? { inviteToken } : {}),
        },
    })
    useAuthStore.getState().setSession(data.accessToken, data.user)
    return data
}

export async function signOut(): Promise<void> {
    try {
        await apiRequest<void>('/auth/logout', { method: 'POST' })
    } finally {
        useAuthStore.getState().clear()
    }
}

/** Stiller Refresh beim Boot; ohne gültigen Refresh-Cookie → abgemeldet. */
export async function ensureSession(): Promise<void> {
    const ok = await refreshTokens()
    if (!ok) useAuthStore.getState().clear()
}

export function updateProfile(
    body: Partial<Pick<UserProfile, 'name' | 'userLanguage' | 'lastWorkspaceId'>>,
): Promise<UserProfile> {
    return apiRequest<UserProfile>('/users/profile', {
        method: 'PATCH',
        body,
    })
}

// === Passwort / E-Mail / Account-Verwaltung ===

/** Löst die Reset-Mail aus; immer 204 (Anti-Enumeration). */
export function requestPasswordReset(email: string): Promise<void> {
    return apiRequest<void>('/auth/requestPasswordChangeToken', {
        method: 'POST',
        body: { email },
    })
}

export function resetPassword(body: {
    email: string
    resetPasswordToken: string
    newPassword: string
    repeatedPassword: string
}): Promise<{ message: string }> {
    return apiRequest<{ message: string }>('/auth/reset-password', {
        method: 'POST',
        body,
    })
}

/** Invalidiert ALLE Refresh-Tokens → danach signOut() + Redirect /login. */
export function changePassword(
    currentPassword: string,
    newPassword: string,
): Promise<{ message: string }> {
    return apiRequest<{ message: string }>('/auth/change-password', {
        method: 'PATCH',
        body: { currentPassword, newPassword },
    })
}

export function verifyEmail(token: string): Promise<unknown> {
    return apiRequest<unknown>(
        `/auth/verify-email?token=${encodeURIComponent(token)}`,
    )
}

/** Bearer nötig; 204. */
export function resendVerificationEmail(): Promise<void> {
    return apiRequest<void>('/auth/resend-verification-email')
}

export function fetchSignupStatus(): Promise<{ enabled: boolean }> {
    return apiRequest<{ enabled: boolean }>('/auth/signup-status')
}

/** Bestätigungs-Mail geht an die NEUE Adresse; immer 204. */
export function requestEmailChange(
    currentPassword: string,
    newEmail: string,
): Promise<void> {
    return apiRequest<void>('/auth/request-email-change', {
        method: 'POST',
        body: { currentPassword, newEmail },
    })
}

export function confirmEmailChange(token: string): Promise<unknown> {
    return apiRequest<unknown>(
        `/auth/confirm-email-change?token=${encodeURIComponent(token)}`,
    )
}

/** Löst die Account-Lösch-Mail aus; immer 204 (Anti-Enumeration). */
export function requestDeleteToken(email: string): Promise<void> {
    return apiRequest<void>('/auth/requestDeleteToken', {
        method: 'POST',
        body: { email },
    })
}

/** DELETE /auth/:deleteToken — Pfad-Param UND Body {email}. */
export function deleteAccount(
    deleteToken: string,
    email: string,
): Promise<void> {
    return apiRequest<void>(`/auth/${encodeURIComponent(deleteToken)}`, {
        method: 'DELETE',
        body: { email },
    })
}
